/**
 * Notification Helper Component
 *
 * Interactive testing and demonstration page for the global notification system.
 * Use this page to test all notification features and see implementation examples.
 */

import React from "react";
import { notificationService } from "@/services/notificationService";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";

const NotificationHelper: React.FC = () => {
  // Simulate API call
  const simulateApiCall = (
    shouldSucceed: boolean = true
  ): Promise<{ id: string; name: string }> => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        if (shouldSucceed) {
          resolve({ id: "123", name: "Test Item" });
        } else {
          reject(new Error("API request failed"));
        }
      }, 2000);
    });
  };

  // Quick Actions - Basic notification types
  const showSuccessNotification = () => {
    notificationService.success("Operation completed", "Your changes have been saved successfully");
  };

  const showErrorNotification = () => {
    notificationService.error("Operation failed", "Something went wrong. Please try again.");
  };

  const showInfoNotification = () => {
    notificationService.info("System update available", "A new version is ready to install");
  };

  const showWarningNotification = () => {
    notificationService.warning("Unsaved changes", "You have unsaved changes that will be lost");
  };

  // Loading notification
  const showLoadingNotification = () => {
    const toastId = notificationService.loading("Processing your request...");

    setTimeout(() => {
      notificationService.dismiss(toastId);
      notificationService.success("Request completed!");
    }, 3000);
  };

  // Interactive Toasts - Notifications with action buttons
  const showNotificationWithAction = () => {
    notificationService.info("New update available", "Click to refresh and apply the update", {
      action: {
        label: "Refresh",
        onClick: () => {
          notificationService.success("Refreshing application...");
          // window.location.reload();
        },
      },
    });
  };

  const showNotificationWithCancel = () => {
    notificationService.warning("Delete this item?", "This action cannot be undone", {
      action: {
        label: "Delete",
        onClick: () => {
          notificationService.success("Item deleted successfully");
        },
      },
      cancel: {
        label: "Cancel",
        onClick: () => {
          notificationService.dismiss();
        },
      },
    });
  };

  // Promise-based notification
  const showPromiseNotification = () => {
    notificationService.promise(simulateApiCall(true), {
      loading: "Creating incident...",
      success: (data) => `Incident ${data.id} created successfully`,
      error: "Failed to create incident",
    });
  };

  // Notification Helpers - Pre-built notification methods
  const showApiResponseHandler = async () => {
    try {
      const result = await simulateApiCall(Math.random() > 0.5);
      notificationService.apiResponse(
        true,
        "Data fetched successfully",
        `Retrieved ${result.name}`
      );
    } catch (error) {
      notificationService.apiResponse(false, "Failed to fetch data", "Please try again later");
    }
  };

  const showCreateOperation = () => {
    notificationService.dataOperation("create", "incident", true);
  };

  const showUpdateOperation = () => {
    notificationService.dataOperation("update", "problem", true);
  };

  const showDeleteOperation = () => {
    notificationService.dataOperation("delete", "integration", false);
  };

  // Error Handling - Specialized error notifications
  const showNetworkError = () => {
    const error = new Error("Connection timeout");
    notificationService.networkError(error);
  };

  const showValidationError = () => {
    notificationService.validationError("Form validation failed", [
      "email",
      "password",
      "username",
    ]);
  };

  const showPermissionError = () => {
    notificationService.permissionError("delete this resource");
  };

  // Positioning Options - Control where notifications appear
  const showTopLeftNotification = () => {
    notificationService.info("Top Left Position", "This notification appears at top-left", {
      position: "top-left",
    });
  };

  const showBottomRightNotification = () => {
    notificationService.success(
      "Bottom Right Position",
      "This notification appears at bottom-right",
      {
        position: "bottom-right",
      }
    );
  };

  // Timing Controls - Custom duration settings
  const showShortDurationNotification = () => {
    notificationService.info("Quick message", "This will disappear in 2 seconds", {
      duration: 2000,
    });
  };

  const showLongDurationNotification = () => {
    notificationService.warning("Important notice", "This will stay for 10 seconds", {
      duration: 10000,
    });
  };

  // Utility actions
  const dismissAllNotifications = () => {
    notificationService.dismissAll();
    notificationService.success("All notifications cleared");
  };

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Notification Helper</h1>
        <p className="text-gray-600">
          Interactive testing page for the global notification system. Click buttons below to see
          different notification types in action.
        </p>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Basic notification types for common scenarios</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button onClick={showSuccessNotification} variant="default">
            Success
          </Button>
          <Button onClick={showErrorNotification} variant="destructive">
            Error
          </Button>
          <Button onClick={showInfoNotification} variant="outline">
            Info
          </Button>
          <Button onClick={showWarningNotification} variant="outline">
            Warning
          </Button>
          <Button onClick={showLoadingNotification} variant="outline">
            Loading
          </Button>
        </CardContent>
      </Card>

      {/* Interactive Toasts */}
      <Card>
        <CardHeader>
          <CardTitle>Interactive Toasts</CardTitle>
          <CardDescription>Notifications with action buttons for user interaction</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button onClick={showNotificationWithAction} variant="default">
            With Action Button
          </Button>
          <Button onClick={showNotificationWithCancel} variant="outline">
            With Cancel Button
          </Button>
          <Button onClick={showPromiseNotification} variant="outline">
            Promise Notification
          </Button>
        </CardContent>
      </Card>

      {/* Notification Helpers */}
      <Card>
        <CardHeader>
          <CardTitle>Notification Helpers</CardTitle>
          <CardDescription>Pre-built helper methods for common operations</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button onClick={showApiResponseHandler} variant="default">
            API Response
          </Button>
          <Button onClick={showCreateOperation} variant="outline">
            Create Operation
          </Button>
          <Button onClick={showUpdateOperation} variant="outline">
            Update Operation
          </Button>
          <Button onClick={showDeleteOperation} variant="outline">
            Delete Operation (Failed)
          </Button>
        </CardContent>
      </Card>

      {/* Error Handling */}
      <Card>
        <CardHeader>
          <CardTitle>Error Handling</CardTitle>
          <CardDescription>Specialized error notification patterns</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button onClick={showNetworkError} variant="destructive">
            Network Error
          </Button>
          <Button onClick={showValidationError} variant="destructive">
            Validation Error
          </Button>
          <Button onClick={showPermissionError} variant="destructive">
            Permission Error
          </Button>
        </CardContent>
      </Card>

      {/* Positioning Options */}
      <Card>
        <CardHeader>
          <CardTitle>Positioning Options</CardTitle>
          <CardDescription>Control where notifications appear on screen</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button onClick={showTopLeftNotification} variant="outline">
            Top Left
          </Button>
          <Button onClick={showBottomRightNotification} variant="outline">
            Bottom Right
          </Button>
        </CardContent>
      </Card>

      {/* Timing Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Timing Controls</CardTitle>
          <CardDescription>Control how long notifications stay visible</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button onClick={showShortDurationNotification} variant="outline">
            Short Duration (2s)
          </Button>
          <Button onClick={showLongDurationNotification} variant="outline">
            Long Duration (10s)
          </Button>
        </CardContent>
      </Card>

      {/* Utility Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Utility Actions</CardTitle>
          <CardDescription>Control and manage active notifications</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button onClick={dismissAllNotifications} variant="destructive">
            Dismiss All Notifications
          </Button>
        </CardContent>
      </Card>

      {/* Code Reference */}
      <Card>
        <CardHeader>
          <CardTitle>Implementation Reference</CardTitle>
          <CardDescription>How to use the notification service in your code</CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
            {`// Import the service
import { notificationService } from '@/services/notificationService';

// Quick Actions
notificationService.success('Success!', 'Operation completed');
notificationService.error('Error!', 'Something went wrong');
notificationService.info('Info', 'New information available');
notificationService.warning('Warning', 'Please review this');

// Interactive Toasts
notificationService.info('Update available', 'Click to refresh', {
  action: {
    label: 'Refresh',
    onClick: () => window.location.reload()
  }
});

// Notification Helpers
notificationService.dataOperation('create', 'incident', true);
notificationService.apiResponse(true, 'Success', 'Data loaded');

// Error Handling
notificationService.networkError(error);
notificationService.validationError('Invalid data', ['email']);
notificationService.permissionError('delete this item');

// Promise Tracking
notificationService.promise(apiCall(), {
  loading: 'Loading...',
  success: 'Done!',
  error: 'Failed!'
});`}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationHelper;
